import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LANGUAGE, ROLE, DATE_FORMAT } from '../../../_constants/Const';
import { GetUserInfo, PutUserInfo } from '../../../services/apiTypes';

import UserOrganizationTree from './UserOrganizationTree';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import { MaxLength } from '../../../_constants/maxLengthConst';
import { PARTS_LIST_DISPLAY_PATTERN } from '../../../_constants/manageConst';
import { isStringInput } from '../../../_utils/utils';

type UserEditModalProps = {
  errorMsg: string;
  updateErrorMsg: (data: string) => void;
  isOpen: boolean;
  userUpdate?: GetUserInfo;
  handleUserUpdate: (putTargetUser: PutUserInfo) => void;
  handleDialogClose: () => void;
};

const styleButton = {
  width: '120px',
  mx: 2,
  my: 1,
};

const defaultUserUpdateData: PutUserInfo = {
  userId: '',
  name: '',
  roleId: 1,
  companyKey: '',
  language: 'en',
  password: '',
  dateFormat: 0,
  isLocked: false,
  partsListDisplayPattern: 1,
};

const UserEditModal: React.FC<UserEditModalProps> = ({
  errorMsg,
  updateErrorMsg,
  isOpen,
  userUpdate,
  handleUserUpdate,
  handleDialogClose,
}) => {
  const { t } = useTranslation();

  const { accountsInfoData } = useSelector(
    (state: RootState) => state.accountsInfo,
  );

  /** 全てフィールドを確認 */
  const [isDisabledSaveBtn, setIsDisabledSaveBtn] = useState(true);

  /** ユーザーの変更情報を取る */
  const [targetUser, setTargetUser] = useState<PutUserInfo>(
    userUpdate ? { ...userUpdate, password: '' } : defaultUserUpdateData,
  );

  /** パスワードが一致していることを確認 */
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [isChangePassword, setIsChangePassword] = useState(false);
  const isPasswordMismatch = confirmPassword !== targetUser.password;

  useEffect(() => {
    if (userUpdate) {
      setTargetUser({ ...userUpdate, password: '' });
    }
  }, [userUpdate]);

  useEffect(() => {
    if (!isOpen) {
      updateErrorMsg('');
      setTargetUser(defaultUserUpdateData);
      setConfirmPassword('');
      setIsChangePassword(false);
    }
  }, [isOpen]);

  /** 組織ツリー*/
  const setSelectedCompany = (company: string) => {
    setTargetUser((prev) => ({
      ...prev,
      companyKey: company,
    }));
  };
  const selectedCompany = useMemo(
    () =>
      accountsInfoData.companyList.find(
        (item) => item.key === targetUser.companyKey,
      )?.name ?? targetUser.companyKey,
    [accountsInfoData, targetUser.companyKey],
  );

  useEffect(() => {
    if (!isChangePassword) {
      const isAllFilled = Object.entries(targetUser).every(([key, val]) => {
        if (key === 'password') return true;
        return (
          typeof val === 'number' ||
          typeof val === 'boolean' ||
          isStringInput(val)
        );
      });
      setIsDisabledSaveBtn(!isAllFilled);
      return;
    }
    const isAllFilled =
      Object.values(targetUser).every(
        (val) =>
          typeof val === 'number' ||
          typeof val === 'boolean' ||
          isStringInput(val),
      ) && confirmPassword.trim() !== '';
    setIsDisabledSaveBtn(!isAllFilled || isPasswordMismatch);
  }, [targetUser, isChangePassword, confirmPassword, isPasswordMismatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateErrorMsg('');
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
      setConfirmPasswordTouched(true);
      return;
    }
    setTargetUser((prev) => ({
      ...prev,
      [name]:
        typeof prev[name as keyof PutUserInfo] === 'number'
          ? Number(value)
          : value,
    }));
  };

  const updatePasswordForUser = (user: PutUserInfo) => {
    return isChangePassword || !userUpdate
      ? user
      : { ...user, password: targetUser.password };
  };

  /** パスワードを変更するかどうかを確認 */
  const handleIsChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    setIsChangePassword(value);
    if (userUpdate) {
      setTargetUser((prev) => ({
        ...prev,
        password: '',
      }));
      setConfirmPassword('');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      closeAfterTransition={false}
    >
      <DialogTitle>{t('LBL_EDIT')}</DialogTitle>
      <DialogContent>
        {errorMsg && (
          <Typography color="error" sx={{ mb: 1 }}>
            {errorMsg}
          </Typography>
        )}
        <TextField
          autoFocus
          margin="dense"
          label={t('LBL_USER_ID')}
          name="userId"
          fullWidth
          value={targetUser.userId}
          disabled
        />
        <TextField
          margin="dense"
          label={t('LBL_NAME')}
          name="name"
          fullWidth
          value={targetUser.name}
          onChange={handleInputChange}
          slotProps={{ htmlInput: { maxLength: MaxLength.ManageUser.NAME } }}
        />
        <TextField
          margin="dense"
          label={t('LBL_ROLE')}
          name="roleId"
          select
          fullWidth
          value={targetUser.roleId}
          onChange={handleInputChange}
        >
          {Object.keys(ROLE).map((key) => (
            <MenuItem key={key} value={Number(key)}>
              {t(ROLE[Number(key)])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_COMPANY')}
          name="companyKey"
          fullWidth
          value={selectedCompany}
          slotProps={{
            input: {
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <UserOrganizationTree
                    setSelectedCompany={setSelectedCompany}
                    selectedCompanyKey={targetUser.companyKey}
                  />
                </InputAdornment>
              ),
            },
            inputLabel: {
              shrink: !!selectedCompany,
            },
          }}
        />
        <TextField
          margin="dense"
          label={t('LBL_DEFAULT_LANGUAGE')}
          name="language"
          select
          fullWidth
          value={targetUser.language}
          onChange={handleInputChange}
        >
          {Object.keys(LANGUAGE).map((key) => (
            <MenuItem key={key} value={key}>
              {t(LANGUAGE[key])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_DATE_FORMAT')}
          name="dateFormat"
          select
          fullWidth
          value={targetUser.dateFormat}
          onChange={handleInputChange}
        >
          {Object.keys(DATE_FORMAT).map((key) => (
            <MenuItem key={key} value={Number(key)}>
              {t(DATE_FORMAT[Number(key)])}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label={t('LBL_PARTS_DISPLAY_PATTERN')}
          name="partsListDisplayPattern"
          select
          fullWidth
          value={targetUser.partsListDisplayPattern}
          onChange={handleInputChange}
        >
          {Object.values(PARTS_LIST_DISPLAY_PATTERN).map((value) => (
            <MenuItem key={value.id} value={value.id}>
              {t(value.key)}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Checkbox
              checked={isChangePassword}
              onChange={handleIsChangePassword}
            />
          }
          label={t('LBL_CHANGE_PASSWORD')}
        />
        {isChangePassword && (
          <>
            <TextField
              margin="dense"
              label={t('LBL_PASSWORD')}
              name="password"
              type="password"
              autoComplete="new-password"
              fullWidth
              value={targetUser.password}
              onChange={handleInputChange}
              slotProps={{
                htmlInput: { maxLength: MaxLength.ManageUser.PASSWORD },
              }}
            />
            <TextField
              margin="dense"
              label={t('LBL_CONFIRM_PASSWORD')}
              name="confirmPassword"
              type="password"
              autoComplete="off"
              fullWidth
              value={confirmPassword}
              onChange={handleInputChange}
              error={isPasswordMismatch && confirmPasswordTouched}
              helperText={
                isPasswordMismatch && confirmPasswordTouched
                  ? t('ERR_CONFIRM_PASSWORD')
                  : ''
              }
              slotProps={{
                htmlInput: {
                  maxLength: MaxLength.ManageUser.CONFIRM_PASSWORD,
                },
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          sx={styleButton}
          variant="contained"
          onClick={handleDialogClose}
        >
          {t('BTN_CANCEL')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          sx={styleButton}
          onClick={() => {
            updateErrorMsg('');
            handleUserUpdate(updatePasswordForUser(targetUser));
          }}
          disabled={isDisabledSaveBtn}
        >
          {t('BTN_SAVE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEditModal;
